import { useState, useEffect } from 'react';

// ─── App Root ─────────────────────────────────────────────────────────────────
// Soft "Install to Home Screen" banner for staff who are using Monarch in
// a regular browser tab instead of the installed PWA. Surfaces on first
// post-login render and persists until dismissed; dismissal sticks for 30
// days. Hidden when the app is already running standalone (already
// installed) or when the platform doesn't support install at all.
export function InstallBanner() {
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState('other');
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Already installed → never show.
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (standalone) return;
    // Recently dismissed → suppress for 30 days.
    try {
      const dismissedAt = Number(localStorage.getItem('monarchInstallDismissedAt') || '0');
      if (dismissedAt && Date.now() - dismissedAt < 30 * 24 * 3600 * 1000) return;
    } catch (_) {}
    // Detect platform.
    const ua = navigator.userAgent || '';
    const isIOS = /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
    const isAndroid = /Android/i.test(ua);
    if (isIOS) setPlatform('ios');
    else if (isAndroid) setPlatform('android');
    else setPlatform('desktop');

    // Capture the native install prompt (Chrome/Edge/Android). iOS Safari
    // never fires this — for iOS we just show the manual instructions.
    function onBeforeInstall(e) {
      e.preventDefault();
      setDeferredPrompt(e);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    setShow(true);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  function dismiss() {
    setShow(false);
    try { localStorage.setItem('monarchInstallDismissedAt', String(Date.now())); } catch (_) {}
  }

  async function install() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try { await deferredPrompt.userChoice; } catch (_) {}
    setDeferredPrompt(null);
    dismiss();
  }

  if (!show) return null;

  return (
    <div style={{ position: 'fixed', left: 12, right: 12, bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))',
                  zIndex: 200, maxWidth: 480, margin: '0 auto',
                  background: '#1c1f1d', color: '#fff', borderRadius: 14,
                  padding: 14, boxShadow: '0 12px 28px rgba(0,0,0,0.35)',
                  display: 'flex', alignItems: 'center', gap: 12, fontFamily: 'inherit' }}>
      <img src="/assets/monarch-icon-192.png" alt="" style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 10 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Install Monarch</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)' }}>
          {platform === 'ios' ? (
            <>Tap <strong>Share</strong> in Safari → <strong>Add to Home Screen</strong>. Push notifications and offline launch require the installed app.</>
          ) : platform === 'android' ? (
            <>Add Monarch to your home screen for one-tap launch + push notifications.</>
          ) : (
            <>Install Monarch for one-tap launch + native notifications.</>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
        {deferredPrompt && (
          <button onClick={install}
            style={{ background: '#b48a47', color: '#1c1f1d', border: 'none', borderRadius: 8,
                      padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            Install
          </button>
        )}
        <button onClick={dismiss}
          style={{ background: 'transparent', color: 'rgba(255,255,255,0.55)', border: 'none',
                    fontSize: 11, cursor: 'pointer', padding: '4px 8px', fontFamily: 'inherit' }}>
          Not now
        </button>
      </div>
    </div>
  );
}
